import { DeviceTypeIds, GW3Read, GW3ReadDTO, INetworkInterface, IWirelessNetworkInterface } from '@ncss/models/dist/v3';
import { BLE_SERVICES, DirectConnectBaseDevice, VisibleDevice } from '../../base/directConnectBaseDevice';
import { ByteList } from 'byte-list';
import { DirectConnectGateway, InterfaceType, StatusCode, WifiNetwork, WifiSignalStrength } from '../gateway';
import { MessageMaskType } from '@ncss/message';
import { GatewayMessage, GatewayMessageTypes } from './messages/gatewayMessage';
import { GatewayInfoMessage, GatewayInfoMessageTypes } from './messages/gatewayInfoMessage';
import { GatewayLogMessage, GatewayLogMessageTypes } from './messages/gatewayLogMessage';
import { NetworkMessage, NetworkMessageTypes } from './messages/networkMessage';
import { shallowRef, ShallowRef } from 'vue';
import { watchUntil } from '@/utils/watchUntil';
import * as colors from '@/theme/colors.json';

export class DirectConnectGW3 extends DirectConnectBaseDevice implements DirectConnectGateway {
  static Init() {
    DirectConnectBaseDevice.RegisterType(DeviceTypeIds.GATEWAY_301, DirectConnectGW3);
  }

  protected serviceId = BLE_SERVICES.GW301.ID;
  protected notifyCharacteristicId = BLE_SERVICES.GW301.CHARACTERISTICS.NOTIFY;
  protected writeCharacteristicId = BLE_SERVICES.GW301.CHARACTERISTICS.WRITE;

  get loading() {
    return this._loading.value;
  }
  get read() {
    return this._read.value;
  }
  get remoteTarget() {
    return this._remoteTarget.value;
  }
  get availableNetworks() {
    return this._availableNetworks.value;
  }
  get syncStatus() {
    return this._syncStatus.value;
  }
  get syncProgress() {
    return this._syncProgress.value;
  }
  get forcingCellular() {
    return this.read?.forcingCellular();
  }
  get partying() {
    return this.read?.partyModeEnabled();
  }

  get powerLedColor() {
    const powerLoss = this.read?.gatewayPowerLoss();
    const cloudConnected = this.read?.cloudConnected();
    const partying = this.partying;
    if (powerLoss) {
      return colors.red['500'];
    } else if (partying) {
      return colors.orange['500'];
    } else if (cloudConnected) {
      return colors.blue['500'];
    }
    return colors.green['500'];
  }

  get programmedLedColor() {
    const programmed = this.read?.programmedToProperty();
    const partying = this.partying;
    if (partying) {
      return colors.cyan['500'];
    } else if (programmed) {
      return colors.green['500'];
    }
    return colors.yellow['500'];
  }

  get rfLedColor() {
    return this.partying ? colors.yellow['500'] : 'transparent';
  }

  get cellularLedColor() {
    const cellReachable = this.read?.flags?.cellularCloudConnected();
    const cellSignal = this.read ? this.read.flags.cellularSignal() : 0;
    if (this.partying) {
      return colors.violet['500'];
    }
    if (!cellReachable) {
      return 'transparent';
    } else if (cellSignal >= 3) {
      return colors.green['500'];
    } else if (cellSignal >= 2) {
      return colors.yellow['500'];
    } else if (cellSignal >= 0) {
      return colors.red['500'];
    }
    return 'transparent';
  }

  private _loading: ShallowRef<boolean>;
  private _read: ShallowRef<GW3Read | undefined>;
  private _remoteTarget: ShallowRef<{ host: string; port: number } | undefined>;
  private _availableNetworks: ShallowRef<WifiNetwork[]>;
  private _syncStatus: ShallowRef<StatusCode | undefined>;
  private _syncProgress: ShallowRef<number | undefined>;

  constructor(device: VisibleDevice, useMetric: boolean) {
    super(device, useMetric);
    this._loading = shallowRef(false);
    this._read = shallowRef();
    this._remoteTarget = shallowRef();
    this._availableNetworks = shallowRef([]);
    this._syncStatus = shallowRef();
    this._syncProgress = shallowRef();
  }

  protected parseManufacturerData(bytes: ByteList): void {}

  protected registerMessages(): void {
    this.messageService.mask = MessageMaskType.EIGHT_BIT;
    this.messageService.registerMessageGroup({
      mask: GatewayMessageTypes.GROUP_MASK,
      messageClass: GatewayMessage,
      handler: this.gatewayMessageHandler.bind(this),
    });
    this.messageService.registerMessageGroup({
      mask: GatewayInfoMessageTypes.GROUP_MASK,
      messageClass: GatewayInfoMessage,
      handler: this.gatewayInfoMessageHandler.bind(this),
    });
    this.messageService.registerMessageGroup({
      mask: GatewayLogMessageTypes.GROUP_MASK,
      messageClass: GatewayLogMessage,
      handler: this.gatewayLogMessageHandler.bind(this),
    });
    this.messageService.registerMessageGroup({
      mask: NetworkMessageTypes.GROUP_MASK,
      messageClass: NetworkMessage,
      handler: this.networkMessageHandler.bind(this),
    });
  }

  protected onConnected(): void {
    super.onConnected();
    this._loading.value = true;
    this.requestInfo();
  }

  async requestInfo() {
    const msg = new GatewayMessage(GatewayMessageTypes.REQUEST_INFO);
    const response = await this.sendMessage<GatewayInfoMessage>(msg, { match: (res) => res.requestId === msg.frameId });
    if (response) {
      this.requestDHCPInfo();
    }
    return response;
  }

  party(enable: boolean): Promise<boolean> {
    const msg = new GatewayInfoMessage(
      enable ? GatewayInfoMessageTypes.ENABLE_PARTY_MODE : GatewayInfoMessageTypes.DISABLE_PARTY_MODE
    );
    return this.sendMessage<GatewayInfoMessage>(msg).then(() => true);
  }

  /**
   * The GW301 does not support setting "Force Cellular" through DirectConnect
   * @param force
   */
  setForceCellular(force: boolean): Promise<boolean> {
    throw new Error('Method not implemented.');
  }

  setCellularEnabled(enabled: boolean): Promise<boolean> {
    const msg = new GatewayInfoMessage(
      enabled ? GatewayInfoMessageTypes.SET_CELLULAR_ENABLED : GatewayInfoMessageTypes.SET_CELLULAR_DISABLED
    );
    return this.sendMessage<GatewayInfoMessage>(msg, {
      match: (res) => res.type === GatewayInfoMessageTypes.INFO_V1 || res.type === GatewayInfoMessageTypes.INFO,
    }).then((res) => !!res);
  }

  async setRemoteTarget(info: { host?: string; port?: number }): Promise<{ host?: string; port?: number } | undefined> {
    const msg = new NetworkMessage(NetworkMessageTypes.CONFIGURE_TARGET_SERVER);
    msg.host = info.host;
    msg.port = info.port;
    const acked: boolean = await this.sendMessage<NetworkMessage>(msg, {
      match: (res) => res.type === NetworkMessageTypes.CONFIGURE_TARGET_SERVER_ACK,
    }).then((res) => !!res);
    if (acked) {
      const info = await this.requestInfo();
      return info ? { host: info.remoteHost, port: info.remotePort } : undefined;
    }
    return undefined;
  }

  changeNetwork(
    interfaceType: InterfaceType,
    netInterface: IWirelessNetworkInterface | INetworkInterface
  ): Promise<IWirelessNetworkInterface | INetworkInterface | undefined> {
    const msg = new NetworkMessage(
      interfaceType === 'wlan' ? NetworkMessageTypes.CONFIGURE_WLAN0 : NetworkMessageTypes.CONFIGURE_ETH0
    );
    msg.useDHCP = netInterface.dhcp;
    if (!msg.useDHCP) {
      msg.ipAddress = netInterface.address;
      msg.gateway = netInterface.gateway;
      msg.netmask = netInterface.netmask;
    }
    return this.sendMessage<NetworkMessage>(msg, {
      match: (res) => {
        return res.type === NetworkMessageTypes.DHCP_STATE;
      },
      retries: 0,
      timeOutSeconds: 15,
    }).then(() => {
      return interfaceType === 'wlan' ? this.read?.wlan : this.read?.eth;
    });
  }

  changeWifiNetwork(ssid: string, password?: string): Promise<boolean> {
    const msg = new NetworkMessage(NetworkMessageTypes.CONNECT_TO_WIFI);
    msg.ssid = ssid;
    if (password) {
      msg.password = password;
    }
    return this.sendMessage<NetworkMessage>(msg, {
      match: (res) => res.type === NetworkMessageTypes.CONNECTED_TO_WIFI,
    }).then((res) => !!res);
  }

  forgetWifiNetwork(): Promise<boolean> {
    return this.sendMessage<NetworkMessage>(new NetworkMessage(NetworkMessageTypes.DISCONNECT_WIFI), {
      match: (res) => res.type === NetworkMessageTypes.DISCONNECTED_WIFI,
    }).then((res) => !!res);
  }

  /**
   * The GW301 does not support forcing a SYNC through DirectConnect
   * @returns
   */
  async forceSync(): Promise<{ success: boolean }> {
    await this.sendMessage<GatewayMessage>(new GatewayMessage(GatewayMessageTypes.FORCE_SYNC));
    return new Promise((resolve) => {
      watchUntil(
        this._syncStatus,
        (done, val) => {
          if (val === StatusCode.SYNC_STOPPED) {
            done();
            resolve({ success: true });
          } else if (val === StatusCode.SYNC_FAILED) {
            done();
            resolve({ success: false });
          }
        },
        {
          timeoutSeconds: 30,
          onTimeout: () => {
            resolve({ success: false });
          },
        }
      );
    });
  }

  private gatewayMessageHandler(msg: GatewayMessage) {
    if (msg.type === GatewayMessageTypes.SYNC_STARTED) {
      this._syncStatus.value = StatusCode.SYNC_START;
    } else if (msg.type === GatewayMessageTypes.SYNC_FINISHED) {
      this._syncStatus.value = StatusCode.SYNC_STOPPED;
    }
    this.checkResponse(msg);
  }

  private gatewayInfoMessageHandler(msg: GatewayInfoMessage) {
    this._loading.value = false;
    const oldRead = this._read.value;
    this._read.value = this.createGW3Read(msg);
    if (this.read?.wlan?.connectedSSID && !this._availableNetworks.value?.length) {
      const { connectedSSID } = this.read.wlan;
      this._availableNetworks.value = [{ name: connectedSSID, secure: false, signal: MapRssiToWifiSignal(0) }];
    }
    if (oldRead?.wlan?.address !== this.read?.wlan?.address || oldRead?.eth?.address !== this.read?.eth?.address) {
      this.requestDHCPInfo();
    }
    this._remoteTarget.value = {
      host: msg.remoteHost || '',
      port: msg.remotePort || 0,
    };
    this.checkResponse(msg);
  }

  private gatewayLogMessageHandler(msg: GatewayLogMessage) {
    this.checkResponse(msg);
  }

  private requestDHCPInfo() {
    const msg = new NetworkMessage(NetworkMessageTypes.REQUEST_DHCP_STATE);
    return this.sendMessage<NetworkMessage>(msg, { match: (res) => res.type === NetworkMessageTypes.DHCP_STATE });
  }

  private networkMessageHandler(msg: NetworkMessage) {
    if (msg.type === NetworkMessageTypes.AVAILABLE_NETWORKS) {
      this._availableNetworks.value = msg.availableNetworks
        .filter((s) => !!s.ssid)
        .map((s) => {
          return {
            name: s.ssid || '',
            secure: !!s.securityType,
            signal: MapRssiToWifiSignal(s.rssi || 0),
          };
        });
    } else if (msg.type === NetworkMessageTypes.DHCP_STATE && this.read) {
      const dto = this.read.toDTO();
      const eth: INetworkInterface = { ...(dto.eth || {}), dhcp: msg.isEthUsingDHCP };
      const wlan: INetworkInterface = { ...(dto.wlan || {}), dhcp: msg.isWlanUsingDHCP };
      if (!eth.dhcp) {
        eth.address = msg.ethIpAddress;
        eth.gateway = msg.ethGateway;
        eth.netmask = msg.ethNetmask;
      }
      if (!wlan.dhcp) {
        wlan.address = msg.wlanIpAddress;
        wlan.gateway = msg.wlanGateway;
        wlan.netmask = msg.wlanNetmask;
      }
      dto.eth = eth;
      dto.wlan = wlan;
      this._read.value = new GW3Read(dto);
    }
    // else if (msg.type === NetworkMessageTypes.DISCONNECTED_WIFI) {
    //   // TODO
    // } else if (msg.type === NetworkMessageTypes.CONNECTED_TO_WIFI) {
    //   // TODO
    // } else if (msg.type === NetworkMessageTypes.ERROR) {
    //   // TODO
    // } else if (msg.type === NetworkMessageTypes.CONFIGURE_ETH0_ACK) {
    //   // TODO
    // } else if (msg.type === NetworkMessageTypes.CONFIGURE_WLAN0_ACK) {
    //   // TODO
    // } else if (msg.type === NetworkMessageTypes.CONFIGURE_TARGET_SERVER_ACK) {
    //   // TODO
    // }
    this.checkResponse(msg);
  }

  private createGW3Read(msg: GatewayInfoMessage) {
    if (!msg.lastSyncedAt) {
      throw new Error('Could not create a valid GW3Read without a lastSyncedAt');
    }
    const gatewayRead: GW3ReadDTO = {
      deviceId: msg.serialNumber,
      lastCheckIn: msg.lastSyncedAt,
      flags: msg.flags,
      resetCount: msg.resetCount,
      cloudNetworkStatus: {
        flags: msg.cloudNetworkStatusFlags,
        error: msg.cloudNetworkStatusError,
      } as any,
      cellularNetworkStatus: {
        flags: msg.cellularFlags,
        signalQuality: msg.cellularSignalQuality,
        registeredNetwork: msg.cellularRegisteredNetwork,
        error: msg.cellularError,
      } as any,
      cellularFlags: msg.cellularFlags,
      osUpTime: msg.osUpTime,
      appUpTime: msg.appUpTime,
      cpuUsage: msg.cpuUsage as any,
      memoryUsage: msg.memoryUsage,
      lastReadsSyncedUpAt: msg.lastSyncedAt,
      wlan: msg.wlan,
      eth: msg.eth,
    };
    return new GW3Read(gatewayRead);
  }
}

function MapRssiToWifiSignal(rssi: number) {
  if (rssi < 30) {
    return WifiSignalStrength.POOR;
  } else if (rssi < 60) {
    return WifiSignalStrength.FAIR;
  }
  return WifiSignalStrength.EXCELLENT;
}
